import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { cn } from '~/lib/cn';

import type { RelatedTrack } from '../../../api/related-tracks';
import { getRelatedTracks } from '../../../api/related-tracks';
import { TableTd, TableTh } from './Table';

export interface RelatedTracksProps {
  isrc: string;
  mediaCode?: string;
  className?: string;
}

function RelatedTracks(props: RelatedTracksProps) {
  const { t } = useTranslation('label-perf');
  const [data, setData] = useState<RelatedTrack[]>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    (async (): Promise<void> => {
      try {
        setLoading(true);

        const relatedTracks = await getRelatedTracks(props.isrc);
        const relatedTracksFiltered = props.mediaCode
          ? relatedTracks.filter((track) => track.mediaCode === props.mediaCode)
          : relatedTracks;

        setData(relatedTracksFiltered);
      } catch (_error) {
        setError((_error as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [props.isrc, props.mediaCode]);

  if (loading) {
    return (
      <div className="text-body-sm">
        <Icon
          type="spinner"
          variant="solid"
          size="sm"
          className="fa-spin mr-sm"
        />{' '}
        {t('body.loading')}
      </div>
    );
  }

  if (error) {
    return <p className="text-body-sm text-error-default">{error}</p>;
  }

  if (data?.length === 0) {
    return (
      <div className="text-body-sm">
        <Icon type="music" variant="solid" size="sm" className="mr-sm" />{' '}
        {t('noData.noRelatedTracks')}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-sm border border-emphasis-subtle">
      <table className="w-full">
        <thead>
          <tr className="bg-level-02">
            <TableTh>{t('table.ISRC')}</TableTh>
            <TableTh>{t('table.title')}</TableTh>
            <TableTh>{t('table.version')}</TableTh>
            <TableTh>{t('table.audioPres')}.</TableTh>
            <TableTh>{t('table.assetType')}</TableTh>
            <TableTh>{t('table.assetSubType')}</TableTh>
            <TableTh>{t('table.mediaCode')}</TableTh>
            <TableTh>{t('table.paFlag')}</TableTh>
          </tr>
        </thead>
        <tbody>
          {data?.map((item, i) => {
            return (
              <tr
                className={cn(
                  'h-[3.2em] border border-emphasis-subtle',
                  i % 2 === 0 ? 'bg-level-03' : 'bg-level-02',
                )}
                key={item.isrc}
              >
                <TableTd>
                  <a
                    className="cursor-pointer underline text-default"
                    href={`/dashboard?id=${item.isrc}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {item.isrc}

                    <Icon
                      type="external-link"
                      variant="solid"
                      size="sm"
                      className="ml-sm"
                    />
                  </a>
                </TableTd>
                <TableTd className={cn(item.isTwin && 'font-semibold')}>
                  {item.title ?? '--'}
                </TableTd>
                <TableTd>{item.version ?? '--'}</TableTd>
                <TableTd>{item.audioPresentation ?? '--'}</TableTd>
                <TableTd>{item.recType ?? '--'}</TableTd>
                <TableTd>{item.recSubType ?? '--'}</TableTd>
                <TableTd>{item.mediaCode ?? '--'}</TableTd>
                <TableTd>{item.paFlag ? 'Yes' : 'No'}</TableTd>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

RelatedTracks.displayName = 'RelatedTracks';

export { RelatedTracks };
